/**
 * 素材の取得元 (source_type) に関するラベル・色分類・フィルタ構造の共通定義。
 * エンドユーザーの素材モーダル・素材パネルのフィルタ・org-admin の素材画面の
 * すべてから参照する正本。
 * 新しい source_type を増やすときは、まずこのファイル (特に SOURCE_TYPE_LABELS) を更新する。
 */
import { brandConfig } from '../../config/brands';

/** 取得元フィルタに混ぜる用途の疑似コードの接頭辞 ('audio_role:bgm' など)。 */
const AUDIO_ROLE_SOURCE_PREFIX = 'audio_role:';

export const SOURCE_TYPE_LABELS: Record<string, string> = {
  generated: 'AI生成画像',
  upload: 'アップロード',
  url: 'URL 取込',
  asin: 'Amazon商品画像',
  camera: 'カメラ',
  url_capture: 'スクリーンショット',
  logo: 'ロゴ',
  // バナーレイアウトのチーム共有 (ガイド PNG・docs/features/banner-layout-template-plan.md §8 段階2)
  banner_layout: 'バナーレイアウト',
  transform: '画像編集',
  canva: 'Canva',
  manual: '手動登録',
  // パートナー (OEM) ごとの商品呼称を反映: default='商品登録画像' / hypeapp='プロジェクト登録画像'
  manual_product: `${brandConfig.labels.productShort}登録画像`,
  scene_video: 'AI生成動画',
  video_last_frame: '動画ラストフレーム',
  narration: 'ナレーション',
  bgm: 'BGM',
  // 旧シナリオ編集ページの書き出し。現在は動画エディタの書き出し (video_edit_render) と同じ扱いで表示する
  preview_video: '動画エディタ',
  actor_person: '俳優ポートレート',
  actor_character_sheet: 'キャラクターシート',
  concat_video: '連結プレビュー動画',
  video_edit_render: '動画エディタ',
  scene_motion: 'モーショングラフィックス',
  scenario_motion: 'モーショングラフィックス (全編)',
  motion_layered: 'モーショングラフィックス (レイヤー)',
  brand_cut_motion: 'ブランドカット',
  video_first_frame: '動画ファーストフレーム',
  sound_logo: 'サウンドロゴ',
  // 効果音とフリー楽曲 (BGM) の両方のカタログ取り込み。個別の素材は labelMaterialSource で用途別に出し分ける
  audio_catalog: '効果音・フリー楽曲',
  // 動画素材から取り出した音声 (動画エディタの効果音ピッカー。用途は効果音)
  video_audio_extract: '動画の音声',
  storyboard_attached_tile: '絵コンテ添付画像',
  face_privacy_sanitized: '覆面化画像',
  // 旧 AI 生成画像 (2026-02 以前の source_type)
  generation: 'AI生成画像',
  scenario: 'シナリオフレーム',
  scenario_storyboard: 'ストーリーボード',
  custom: 'カスタム',
  veo_gcs: 'AI生成動画',
  // 用途 (default_audio_role) の疑似コード。list_materials が 'audio_role:<用途>' を用途の一致として扱い、
  // ファセットにも同じ形で返す (20261002_01_list_materials_audio_role_filter.sql)。
  // アップロードした音声は source_type='upload' なので、用途でしか BGM / 声 / 効果音を見分けられない
  [`${AUDIO_ROLE_SOURCE_PREFIX}voice`]: 'ナレーション',
  [`${AUDIO_ROLE_SOURCE_PREFIX}bgm`]: 'BGM',
  [`${AUDIO_ROLE_SOURCE_PREFIX}sound_effect`]: '効果音',
  [`${AUDIO_ROLE_SOURCE_PREFIX}sound_logo`]: 'サウンドロゴ',
};

export function labelSourceType(code: string | null | undefined): string {
  if (!code) return '—';
  return SOURCE_TYPE_LABELS[code] ?? code;
}

/**
 * 素材 1 件の取得元ラベル。source_type だけでは用途が決まらないもの (カタログ取り込みの
 * 効果音 / フリー楽曲) を default_audio_role で出し分ける。フィルタの選択肢は labelSourceType。
 */
export function labelMaterialSource(material: {
  source_type?: string | null;
  default_audio_role?: string | null;
}): string {
  if (material.source_type === 'audio_catalog') {
    return material.default_audio_role === 'bgm' ? 'フリー楽曲' : '効果音';
  }
  return labelSourceType(material.source_type);
}

/**
 * 取得元バッジの色分類。CSS 側で .is-{variant} クラスから配色する。
 * - ai      : AI 生成 (indigo)
 * - upload  : ユーザー登録 / 取込 (slate)
 * - amazon  : Amazon 由来 (orange)
 * - video   : 動画 (red)
 * - audio   : 音声 (green)
 * - actor   : AI 俳優 (purple)
 * - scenario: シナリオ (teal)
 * - canva   : Canva 取込 (violet)
 */
export type SourceTypeVariant =
  | 'ai'
  | 'upload'
  | 'amazon'
  | 'video'
  | 'audio'
  | 'actor'
  | 'scenario'
  | 'canva'
  | 'default';

const SOURCE_TYPE_VARIANTS: Record<string, SourceTypeVariant> = {
  generated: 'ai',
  veo_gcs: 'video',
  upload: 'upload',
  url: 'upload',
  url_capture: 'upload',
  logo: 'upload',
  banner_layout: 'upload',
  canva: 'canva',
  manual: 'upload',
  manual_product: 'upload',
  custom: 'upload',
  asin: 'amazon',
  scene_video: 'video',
  video_last_frame: 'video',
  preview_video: 'video',
  concat_video: 'video',
  video_edit_render: 'video',
  scene_motion: 'video',
  scenario_motion: 'video',
  motion_layered: 'video',
  brand_cut_motion: 'video',
  video_first_frame: 'video',
  sound_logo: 'audio',
  audio_catalog: 'audio',
  video_audio_extract: 'audio',
  storyboard_attached_tile: 'scenario',
  face_privacy_sanitized: 'ai',
  generation: 'ai',
  narration: 'audio',
  bgm: 'audio',
  actor_person: 'actor',
  actor_character_sheet: 'actor',
  scenario: 'scenario',
  scenario_storyboard: 'scenario',
};

export function sourceTypeVariant(code: string | null | undefined): SourceTypeVariant {
  if (!code) return 'default';
  return SOURCE_TYPE_VARIANTS[code] ?? 'default';
}

/**
 * 取得元フィルタの大分類。optgroup のグルーピングと表示順に使う。
 */
export type SourceCategory = 'image' | 'video' | 'scenario' | 'audio' | 'actor' | 'other';

/** 大分類の表示順とラベル。フィルタの optgroup はこの順で並ぶ。 */
export const SOURCE_CATEGORY_ORDER: { key: SourceCategory; label: string }[] = [
  { key: 'image', label: '静止画' },
  { key: 'video', label: '動画' },
  { key: 'scenario', label: 'シナリオ' },
  { key: 'audio', label: 'オーディオ' },
  { key: 'actor', label: '俳優' },
  { key: 'other', label: 'その他' },
];

/**
 * 取得元フィルタの 1 選択肢 = 1 エントリ。
 * codes が複数なら、その選択肢は source_type の OR 絞り込みになる。
 * 表示ラベルは labelSourceType(codes[0]) を使う。
 * 配列の記述順がそのままフィルタの表示順 (カテゴリ内の並び) になるため、
 * UI 上で自然に見える順序を意図して並べること (英語コード順ではない)。
 * divider: true のエントリは、フラット表示 (素材パネル) でその直前に区切り線を入れる
 * = カテゴリ内のサブグループの先頭。optgroup 表示 (org-admin) では無視される。
 */
/**
 * 音声の用途ごとの取得元フィルタの値 (選択肢の value = codes を ',' 連結したもの)。
 * 素材ピッカーの初期値 (initialSourceTypes) にはこの値を渡す。
 */
export const AUDIO_PURPOSE_SOURCE_FILTER = {
  voice: `narration,${AUDIO_ROLE_SOURCE_PREFIX}voice`,
  bgm: `bgm,${AUDIO_ROLE_SOURCE_PREFIX}bgm`,
  sound_effect: `${AUDIO_ROLE_SOURCE_PREFIX}sound_effect`,
  sound_logo: `sound_logo,${AUDIO_ROLE_SOURCE_PREFIX}sound_logo`,
} as const;

type SourceFilterEntry = { category: Exclude<SourceCategory, 'other'>; codes: string[]; divider?: boolean };

const SOURCE_FILTER_ENTRIES: SourceFilterEntry[] = [
  // 静止画: AI生成 | 商品系 | 取込・手動 | 加工
  // (旧コード generation は AI生成画像に統合)
  { category: 'image', codes: ['generated', 'generation'] },
  { category: 'image', codes: ['asin'] },
  { category: 'image', codes: ['manual_product'] },
  { category: 'image', codes: ['upload'] },
  { category: 'image', codes: ['manual'] },
  { category: 'image', codes: ['camera'] },
  { category: 'image', codes: ['url_capture'] },
  { category: 'image', codes: ['transform'] },
  { category: 'image', codes: ['canva'] },
  { category: 'image', codes: ['logo'] },
  { category: 'image', codes: ['banner_layout'] },
  // 動画: AI生成動画 (scene_video と旧 Veo 出力 veo_gcs を統合) | 動画エディタ | モーショングラフィックス
  // (シーン / 全編 / レイヤーを統合) | ブランドカット | 旧シナリオ編集ページの書き出し
  { category: 'video', codes: ['scene_video', 'veo_gcs'] },
  // 旧シナリオ編集ページの書き出し (preview_video) も「動画エディタ」に統合
  { category: 'video', codes: ['video_edit_render', 'preview_video'] },
  { category: 'video', codes: ['scene_motion', 'scenario_motion', 'motion_layered'] },
  { category: 'video', codes: ['brand_cut_motion'] },
  { category: 'video', codes: ['concat_video'], divider: true },
  // シナリオ
  { category: 'scenario', codes: ['scenario'] },
  { category: 'scenario', codes: ['scenario_storyboard'] },
  // オーディオ: 生成した音声 (source_type) と、同じ用途でアップロードした音声 (用途の疑似コード) をまとめる
  { category: 'audio', codes: AUDIO_PURPOSE_SOURCE_FILTER.voice.split(',') },
  { category: 'audio', codes: AUDIO_PURPOSE_SOURCE_FILTER.bgm.split(',') },
  { category: 'audio', codes: AUDIO_PURPOSE_SOURCE_FILTER.sound_effect.split(',') },
  { category: 'audio', codes: AUDIO_PURPOSE_SOURCE_FILTER.sound_logo.split(',') },
  { category: 'audio', codes: ['video_audio_extract'] },
  // 俳優
  { category: 'actor', codes: ['actor_person'] },
  { category: 'actor', codes: ['actor_character_sheet'] },
];

/**
 * 隠しファイル (内部の中間素材)。一覧 RPC (list_materials) で除外されるためフィルタに出さない。
 * backend/routes/org_admin/materials.py の HIDDEN_FRAME_SOURCE_TYPES と同じ集合。
 */
const HIDDEN_SOURCE_TYPES = new Set([
  'video_first_frame', 'video_last_frame', 'storyboard_attached_tile', 'face_privacy_sanitized',
  // カタログの効果音 / フリー楽曲を使ったときの自動複製 (20261001_02_hide_audio_catalog_materials.sql)
  'audio_catalog',
]);

export type SourceFilterOption = { value: string; label: string; divider?: boolean };
export type SourceFilterGroup = { key: SourceCategory; label: string; options: SourceFilterOption[] };

/**
 * 実在する source_type 配列から、取得元フィルタのドロップダウン構造を組み立てる。
 * - 各エントリは codes のいずれかが実在するときだけ表示。複数 codes は OR 絞り込み。
 * - どのエントリにも属さない code (url / 残骸コード等) はすべて
 *   「その他」1 選択肢に OR で集約する。
 * - option の value は source_type コードを ',' 連結したもの。
 * - option.divider はカテゴリ内サブグループの先頭を示す (フラット表示用)。
 */
export function buildSourceFilterGroups(available: string[]): SourceFilterGroup[] {
  available = available.filter((c) => !HIDDEN_SOURCE_TYPES.has(c));
  const availableSet = new Set(available);
  const claimed = new Set<string>();
  const byCategory = new Map<SourceCategory, SourceFilterOption[]>();

  for (const entry of SOURCE_FILTER_ENTRIES) {
    entry.codes.forEach((c) => claimed.add(c));
    if (!entry.codes.some((c) => availableSet.has(c))) continue;
    const opts = byCategory.get(entry.category) ?? [];
    // サブグループ先頭でも、そのカテゴリで実在する最初の選択肢には区切り線を付けない
    // (カテゴリ境界の区切り線と重複するため)
    const divider = entry.divider && opts.length > 0;
    opts.push({ value: entry.codes.join(','), label: labelSourceType(entry.codes[0]), ...(divider ? { divider: true } : {}) });
    byCategory.set(entry.category, opts);
  }

  // どのエントリにも属さない実在コード → 「その他」1 選択肢に OR 集約
  const otherCodes = available
    .filter((c) => !claimed.has(c))
    .sort((a, b) => a.localeCompare(b));
  if (otherCodes.length > 0) {
    byCategory.set('other', [{ value: otherCodes.join(','), label: 'その他' }]);
  }

  return SOURCE_CATEGORY_ORDER
    .map(({ key, label }) => ({ key, label, options: byCategory.get(key) ?? [] }))
    .filter((group) => group.options.length > 0);
}
